import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDispatch, useSelector } from 'react-redux';
import Settings from '@mui/icons-material/Settings';
import { useQuery } from '@tanstack/react-query';
import Logout from '@mui/icons-material/Logout';
import Groups from '@mui/icons-material/Groups';
import HelpOutline from '@mui/icons-material/HelpOutline';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import { WHATS_NEW_PATH } from '../../Utils/changelogView';
import { User } from '../../Objects/interfaces/NavBarInterface';
import { accountType } from '../../Objects/interfaces/accountInterface';
import { useAuthFn } from '../Auth/useAuthFn';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import { auth } from '../../Services/FirebaseService';
import { ENV_DECLARONS, ENV_TEACHR, ENV_VERIFIED_TRAINER, getActiveBrandConfig, isBrandActive, VERIFIED_TRAINER_ACADEMY_URL } from '../../config/environment.config';
import config from '../../config/config';
import { BrandLogo } from '../../Components/shared';
import { clientNavbarConfig, DASHBOARD_NAV_INDEXES } from '../../Components/AppBar/AppBarConfigs';
import { capitalizeFirstLetter } from '../../Utils/Function';
import { avatarFemale, avatarMale } from '../../Utils/Images';
import ImageRepository from '../../Repositories/ImageRepository';
import { FETCH_IMAGE_ACTIVE } from '../../Redux/Actions/UserActions';

interface UseNavigationProps {
    index?: number;
    theme?: 'light' | 'dark';
    isDeclarons?: boolean
}

const useAppBar = (props: UseNavigationProps) => {
    const { index } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { brandConfig, classes } = useTailwindBrand();
    const currentUser = useSelector((state: any) => state?.user?.currentUser) as User;
    const teachr = currentUser?.isTeachr ? currentUser?.teachr : currentUser?.client;
    const isTeachr = currentUser?.isTeachr || false;
    const isClient = (currentUser as any)?.isClient || false;
    const location = useLocation();
    // Espace client : le client connecté a sa propre navigation
    const appConfig = isClient ? clientNavbarConfig : brandConfig.appConfig;
    const [user] = useAuthState(auth);
    const imageSrc = teachr?.profilePicture?.contentUrl
    const setLoading = () => { };
    const { performSignOut } = useAuthFn({ setLoading });

    const { data } = useQuery({
        queryKey: ['fetchProfil', teachr?.profilePicture?.id],
        queryFn: () => ImageRepository.fetchImageById(teachr!.profilePicture.id),
        enabled: !!teachr?.profilePicture?.id,
    });

    useEffect(() => {
        if (data) {
            dispatch({ type: FETCH_IMAGE_ACTIVE, payload: data });
        }
    }, [data, dispatch])

    const isFullyValidatedTeachr = useMemo(() => {
        if (!isTeachr || !teachr) return false;
        return index !== undefined && DASHBOARD_NAV_INDEXES.includes(index);
    }, [isTeachr, teachr, index]);

    const [selectedIndex, setSelectedIndex] = useState<number>(index ?? 0);
    const [scrolling, setScrolling] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

    useEffect(() => {
        if (!user && !currentUser.isAuthenticated && location.pathname.includes(`${getActiveBrandConfig()}/tableau-de-bord`)) {
            return navigate("/");
        }
    }, [user, currentUser, navigate, location.pathname])

    useEffect(() => {
        const handleScroll = () => {
            setScrolling(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleCloseNavMenu = useCallback(() => {
        setAnchorElNav(null);
    }, []);

    const handleNavigation = useCallback((link: string, navIndex?: number, url?: string) => {
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
            handleCloseNavMenu();
            return;
        }
        if (navIndex) {
            setSelectedIndex(navIndex);
        }
        navigate(link);
        handleCloseNavMenu();
    }, [navigate, handleCloseNavMenu]);

    // Logout handler
    const logout = useCallback(() => {
        performSignOut(isTeachr, "/");
        setAnchorEl(null);
    }, [isTeachr, performSignOut]);

    // Gestion du changement de route
    const handleChangeRoute = useCallback(() => {
        if (user) {
            logout();
        } else {
            if (location.pathname === '/') {
                navigate(`/${getActiveBrandConfig()}/register/`);
            } else {
                navigate("/");
            }
        }
    }, [user, logout, navigate, location.pathname]);

    // Configuration du menu compte
    const demoSession = useMemo(() => ({
        user: {
            name: `${capitalizeFirstLetter(teachr?.firstName) || ''} ${capitalizeFirstLetter(teachr?.lastName) || ''}`,
            email: `${teachr?.mail || ''}`,
            image: imageSrc ?? (teachr?.sex === 'MAN' ? avatarMale : avatarFemale),
        },
    }), [teachr, imageSrc]);

    const redirectToParam = useCallback(() => {
        // tableau de bord commun : même URL de paramètres pour prof et client
        handleNavigation(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, 3);
    }, [handleNavigation]);

    const redirectToHelp = useCallback(() => {
        // aide et contact : même page pour prof et client
        handleNavigation(`/${getActiveBrandConfig()}/tableau-de-bord/aide-contact`);
    }, [handleNavigation]);

    const redirectToWhatsNew = useCallback(() => {
        // nouveautés de la version en ligne : page réservée aux profs
        handleNavigation(WHATS_NEW_PATH());
    }, [handleNavigation]);

    const openAcademy = useCallback(() => {
        window.open(VERIFIED_TRAINER_ACADEMY_URL, '_blank', 'noopener,noreferrer');
        setAnchorEl(null);
    }, []);

    // Inscription en cours (étape 2 : identifiants créés, profil pas encore terminé) : le compte n'est pas
    // totalement créé, seul « Se déconnecter » reste disponible, pour toutes les marques.
    const currentRegister = useSelector((state: any) => state?.user?.currentRegister) as number | undefined;
    const registrationInProgress = isTeachr && (!teachr?.id || (currentRegister ?? 1) > 1);

    const accounts: accountType[] = useMemo(() => [
        // L'Academy Verified Trainer est réservée aux coachs : pas d'entrée côté client.
        ...(!registrationInProgress && isTeachr && isBrandActive(ENV_VERIFIED_TRAINER)
            ? [{
                title: "Mon Academy",
                icon: <Groups fontSize="small" />,
                triggle: openAcademy,
            }]
            : []),
        ...(!registrationInProgress
            ? [{
                title: "Paramètres",
                icon: <Settings fontSize="small" />,
                triggle: redirectToParam,
            }, {
                title: "Aide et contact",
                icon: <HelpOutline fontSize="small" />,
                triggle: redirectToHelp,
            },
            // Quoi de neuf : réservé aux profs, pas d'entrée côté client ni élève.
            ...(isTeachr ? [{
                title: "Quoi de neuf",
                icon: <AutoAwesomeOutlined fontSize="small" />,
                triggle: redirectToWhatsNew,
            }] : [])]
            : []),
        {
            title: "Se déconnecter",
            icon: <Logout fontSize="small" />,
            triggle: logout,
        },
    ], [redirectToParam, redirectToHelp, redirectToWhatsNew, logout, openAcademy, registrationInProgress, isTeachr]);

    const handleAccountMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleAccountMenuClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleOpenNavMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    }, []);

    // Affichage du logo
    const displayLogo = useCallback(() => {
        // const variant = props.theme === "dark" ? ( "white") : "default";
        const variant = props.theme === "dark" ? (props.isDeclarons ? "default" : "white") : "default";
        const logoSize = isBrandActive(ENV_DECLARONS) ? "h-[27px] w-[177px]" : isBrandActive(ENV_TEACHR) ? '' : "h-12 w-auto";

        if (props?.index === 5 || props?.index === 6 || props?.index === 7) {
            return (
                <a href={config.uriSite} className="flex items-center">
                    <BrandLogo variant={variant} width={120} className={logoSize} />
                </a>
            );
        } else {
            return (
                <button onClick={() => navigate('/')} className="flex items-center hover:opacity-80 transition-opacity ">
                    <BrandLogo variant={variant} width={120} className={logoSize} />
                </button>
            );
        }
    }, [props.theme, props.isDeclarons, props?.index, navigate]);

    return {
        // States
        selectedIndex,
        scrolling,
        anchorEl,
        anchorElNav,
        user,

        // Config & Data
        brandConfig,
        classes,
        appConfig,
        demoSession,
        accounts,
        isFullyValidatedTeachr,
        isClient,

        // Handlers
        handleNavigation,
        handleChangeRoute,
        handleAccountMenuOpen,
        handleAccountMenuClose,
        handleOpenNavMenu,
        handleCloseNavMenu,
        logout,

        // Location
        location,
        displayLogo
    };
};

export default useAppBar;